import React, { useCallback, useMemo, useState } from 'react';
import { Card } from '@material-ui/core';
import { observer } from 'mobx-react';
import { toJS } from 'mobx';
import { useLazyQuery, useMutation } from '@apollo/client';

import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import getQuoteFriendlySelectedOption from 'offer/helpers/quote-friendly-selected-option';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import {
  SEND_TO_GAYA,
  GET_RATING_WORKSHEET,
  WORKSHEET_RERATE_MESSAGE,
  isRerateError
} from 'core/store/offer-store/offer-queries';
import { Label } from 'core/components/label';
import { track } from 'core/helpers/analytics';
import { types } from 'core/helpers/sanitize';
import { buildAutoXml, buildHomeXml } from './build-ezlynx-xml';
import { downloadFile } from './download-file';
import DownloadButton from './download-button';
import ExportButton from './export-button';
import exportIcon from '../../assets/export.svg';

import useStyles from './documents-sharing.styles';

const ENTITY_LABELS = {
  customer: 'customer',
  car: (count) => (count === 1 ? '1 vehicle' : `${count} vehicles`),
  property: 'property details'
};

function buildGayaSuccessMessage(details) {
  try {
    // details is double-stringified: backend JSON.stringify() + AppSync AWSJSON scalar serialization
    let parsed = JSON.parse(details);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    const { entityCounts } = parsed;
    const parts = [];

    if (entityCounts.customer) {
      parts.push(ENTITY_LABELS.customer);
    }
    if (entityCounts.car) {
      parts.push(ENTITY_LABELS.car(entityCounts.car));
    }
    if (entityCounts.property) {
      parts.push(ENTITY_LABELS.property);
    }

    if (parts.length === 0) {
      return 'Offer data sent to Gaya.';
    }

    let message;
    if (parts.length === 1) {
      message = `${parts[0]} exported to Gaya`;
    } else if (parts.length === 2) {
      message = `${parts[0]} and ${parts[1]} exported to Gaya`;
    } else {
      message = `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]} exported to Gaya`;
    }
    return message.charAt(0).toUpperCase() + message.slice(1);
  } catch {
    return 'Offer data sent to Gaya.';
  }
}

const DocumentsSharing = observer(() => {
  const { offer: offerStore, affinityLookups } = useStore();
  const toast = useToast();
  const { offer, downloadQuote, downloadOffer, status, selectedOption } = offerStore;
  const { clusterId, code, id: offerId, quote } = offer;
  const quoteFriendlyOption = getQuoteFriendlySelectedOption(selectedOption);
  const classes = useStyles();
  const { user, isAgency, isInternalSales, canApproveUWReview, canExportEasyKeyXML } = useSession();

  const affinity = quote?.global?.affinity;
  const affinityData = affinityLookups.data.find((aff) => aff.affinity.startsWith(affinity));
  const agencyAffinity = affinity && affinityData?.isAgency;
  // if agency affinity and internal agent is binding for them, keep agency rep as sales rep
  const dontOverwriteRep = Boolean(!isAgency && agencyAffinity);

  const [sendToGaya, { loading: sendingToGaya }] = useMutation(SEND_TO_GAYA);
  const [fetchRatingWorksheet] = useLazyQuery(GET_RATING_WORKSHEET, { fetchPolicy: 'no-cache' });
  const [downloadingWorksheet, setDownloadingWorksheet] = useState(false);

  const selectedFarmOption = useMemo(
    () => offer.options?.find((o) => o.type === selectedOption),
    [offer.options, selectedOption]
  );

  const allClarionDoorIds = useMemo(() => {
    const farmClarionDoorIds = selectedFarmOption?.farmClarionDoorIds;
    const ids = [];
    if (farmClarionDoorIds?.liabilityClarionDoorId) {
      ids.push(farmClarionDoorIds.liabilityClarionDoorId);
    }
    if (farmClarionDoorIds?.risksClarionDoorIds) {
      for (const risk of farmClarionDoorIds.risksClarionDoorIds) {
        ids.push(risk.clarionDoorId);
      }
    }
    return ids;
  }, [selectedFarmOption]);

  const handleDownloadRatingWorksheet = useCallback(async () => {
    // The worksheet must be fetched under the release this option was rated under. Offers rated
    // before that release was persisted carry none; on the production instance the backend can still
    // locate them (prod-requests path), so let it decide and surface its re-rate message only when it
    // can't (release-pinned instances) (issue #33899).
    const clarionDoorRelease = selectedFarmOption?.clarionDoorRelease;
    setDownloadingWorksheet(true);
    try {
      const { data, error } = await fetchRatingWorksheet({
        variables: { clarionDoorIds: allClarionDoorIds, clarionDoorRelease }
      });
      if (error) {
        throw error;
      }
      if (data?.getRatingWorksheet) {
        window.open(data.getRatingWorksheet, '_blank');
      }
    } catch (err) {
      const message = isRerateError(err) ? WORKSHEET_RERATE_MESSAGE : 'Failed to download rating worksheet. Try again.';
      toast.notify({ type: 'error', message });
    } finally {
      setDownloadingWorksheet(false);
    }
  }, [allClarionDoorIds, selectedFarmOption, fetchRatingWorksheet, toast]);

  const onDownloadQuote = useCallback(() => {
    track('Offer sharing options - Application PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Downloads section'
    });
    downloadQuote({ bypassCache: false });
    toast.notify({
      type: 'success',
      message: 'PDF not correct?',
      label: 'Regenerate from scratch',
      action: () => downloadQuote({ bypassCache: true }),
      durationInSeconds: 30
    });
  }, [downloadQuote, toast, offerId, isAgency]);

  // Matches all farm-based monolines: F (Farm), Z (Home) and Y (Rental). These all use the
  // Zywave DocGen pipeline for the Offer PDF and expose the ClarionDoor rating worksheet —
  // application-handler.js hasFarm() routes all three the same way. FA (Farm-Auto) bundles
  // are not yet supported in the Zywave pipeline.
  const isFarm = isFarmBasedPolicyType(quoteFriendlyOption);

  const onDownloadOffer = useCallback(() => {
    track('Offer sharing options - Offer PDF downloaded', {
      offerId,
      isAgency,
      entryPoint: 'Downloads section'
    });

    if (isFarm) {
      // Farm offers use the Zywave DocGen pipeline for the Quote PDF (not DocRaptor)
      downloadQuote({ bypassCache: false, docContext: 'quote' });
      toast.notify({
        type: 'success',
        message: 'PDF not correct?',
        label: 'Regenerate from scratch',
        action: () => downloadQuote({ bypassCache: true, docContext: 'quote' }),
        durationInSeconds: 30
      });
      return;
    }

    downloadOffer({
      offerId,
      clusterId,
      code,
      policyType: quoteFriendlyOption,
      bypassCache: false,
      revisedQuoteDetails: types.QuoteDetailsInput({
        ...quote,
        rep: quote?.rep?.length && dontOverwriteRep ? quote.rep : user.username
      })
    });
  }, [
    isFarm,
    downloadQuote,
    downloadOffer,
    offerId,
    isAgency,
    clusterId,
    code,
    quoteFriendlyOption,
    user,
    quote,
    dontOverwriteRep,
    toast
  ]);

  const handleExportEzlynx = useCallback(() => {
    try {
      const plainOffer = toJS(offer);
      const optionData = plainOffer.options?.find((o) => o.type === quoteFriendlyOption) || plainOffer.options?.[0];
      const hasAuto = !!selectedOption && selectedOption.includes('A');
      const hasProperty =
        !!selectedOption &&
        (selectedOption.includes('H') ||
          selectedOption.includes('R') ||
          selectedOption.includes('C') ||
          selectedOption === 'Z' ||
          selectedOption === 'Y');

      // Include the insured's name in the filename so downloaded XMLs are
      // identifiable in the user's downloads folder. Strip any non-alphanumeric
      // characters (apostrophes, accents, spaces, etc.) to keep the filename
      // cross-OS safe. Falls back to just the offerId when name is missing.
      const insuredSlug = [quote?.fname, quote?.lname]
        .map((part) =>
          String(part || '')
            .trim()
            .replace(/[^A-Za-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        )
        .filter(Boolean)
        .join('_');
      const namePrefix = insuredSlug ? `${insuredSlug}-` : '';

      if (hasAuto) {
        const autoXml = buildAutoXml({ offer: plainOffer, selectedOption: optionData });
        downloadFile(autoXml, `easykey-auto-${namePrefix}${offerId}.xml`, 'application/xml');
      }
      if (hasProperty) {
        const homeXml = buildHomeXml({ offer: plainOffer, selectedOption: optionData });
        downloadFile(homeXml, `easykey-home-${namePrefix}${offerId}.xml`, 'application/xml');
      }

      toast.notify({
        type: 'success',
        message: `EasyKey XML file${hasAuto && hasProperty ? 's' : ''} downloaded`
      });
    } catch (err) {
      console.error('EasyKey export error:', err);
      toast.notify({
        type: 'error',
        message: "Couldn't generate EasyKey XML files. Try again."
      });
    }
  }, [offer, selectedOption, quoteFriendlyOption, offerId, toast]);

  const handleSendToGaya = useCallback(async () => {
    try {
      const { data } = await sendToGaya({ variables: { id: offerId } });
      if (data?.sendToGaya?.success) {
        toast.notify({
          type: 'success',
          message: buildGayaSuccessMessage(data.sendToGaya.details)
        });
      } else {
        toast.notify({
          type: 'error',
          message: "Couldn't send offer data to Gaya. Try again."
        });
      }
    } catch {
      toast.notify({
        type: 'error',
        message: "Couldn't send offer data to Gaya. Try again."
      });
    }
  }, [offerId, sendToGaya, toast]);

  return (
    <>
      <Label variant="body3" className={classes.downloadHeader}>
        Documents & sharing
      </Label>
      <Card id="offer_downloads" className={classes.pdfDownloadContainer}>
        <DownloadButton
          loading={status === OfferStatus.DownloadingQuote}
          classes={classes}
          onClick={onDownloadQuote}
          downloadText="Application PDF"
        />
        <DownloadButton
          loading={status === OfferStatus.DownloadingOffer}
          classes={classes}
          onClick={onDownloadOffer}
          downloadText="Offer PDF"
        />
        {isFarm && (!isAgency || canApproveUWReview) && allClarionDoorIds.length > 0 && (
          <DownloadButton
            loading={downloadingWorksheet}
            classes={classes}
            onClick={handleDownloadRatingWorksheet}
            downloadText="Rating Worksheet"
          />
        )}
        {isInternalSales && (
          <ExportButton
            loading={sendingToGaya}
            classes={classes}
            icon={exportIcon}
            onClick={handleSendToGaya}
            exportText="Export offer data to Gaya"
            loadingText="Exporting to Gaya..."
          />
        )}
        {canExportEasyKeyXML && (
          <DownloadButton
            loading={false}
            classes={classes}
            onClick={handleExportEzlynx}
            downloadText="Export to EasyKey (XML)"
          />
        )}
      </Card>
    </>
  );
});

export default DocumentsSharing;
